import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, MagnifyingGlass, Buildings, X, Trash, FilePdf, FileText, FileXls, PencilSimple, CheckCircle, Robot, ChartBar, Calendar, CurrencyDollar, LinkSimple, Warning, MagicWand, UploadSimple, DownloadSimple, Square, CheckSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIAS = [
  { v: "bem", l: "Bem/Material" },
  { v: "servico", l: "Serviço" },
  { v: "obra", l: "Obra/Engenharia" },
  { v: "tic", l: "TIC (Tecnologia)" },
];
const catColor = { bem: "#3B82F6", servico: "#F59E0B", obra: "#8B5CF6", tic: "#10B981" };
const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const currentYear = new Date().getFullYear();

const fmtBRL = (v) => `R$ ${(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PAC() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [ano, setAno] = useState(currentYear);
  const [categoria, setCategoria] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showImportar, setShowImportar] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [showTextosIA, setShowTextosIA] = useState(false);

  const load = () => {
    const params = { ano };
    if (categoria) params.categoria = categoria;
    if (status) params.status = status;
    if (search) params.search = search;
    api.get("/pac", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [ano, categoria, status, search]);

  const excluir = async (it) => {
    if (!window.confirm(`Excluir o item Nº ${it.numero_seq} do PAC ${it.ano}?`)) return;
    try { await api.delete(`/pac/${it.id}`); toast.success("Item excluído"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro ao excluir"); }
  };
  const publicar = async (it) => {
    if (!window.confirm(`Publicar item Nº ${it.numero_seq}? Após publicado só o Admin poderá editar.`)) return;
    try { await api.post(`/pac/${it.id}/publicar`); toast.success("Item publicado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro ao publicar"); }
  };
  const exportar = (fmt) => {
    const url = `${API}/pac/export/${fmt}?ano=${ano}`;
    window.open(url, "_blank");
  };

  const gerarPrevio = async () => {
    // Abre modal de seleção de contratos
    setShowSelector(true);
  };

  const totais = useMemo(() => ({
    total_previsto: items.reduce((s, it) => s + (it.valor_total_estimado || 0), 0),
    publicados: items.filter((it) => it.status === "publicado").length,
    rascunhos: items.filter((it) => it.status === "rascunho").length,
  }), [items]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <Calendar size={12} weight="duotone" className="text-[#F59E0B]" />
            Planejamento Anual
          </div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Plano Anual de Contratações</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-3xl">
            Elabore o Plano Anual de Contratações (PAC) conforme <strong className="text-[#E9EEF5]">Art. 12 da Lei 14.133/2021</strong>,
            Decreto 10.947/2022 e IN SEGES/ME nº 21/2022. Cada item pode ser editado, revisado com IA e vinculado a contratos executados.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="pac-importar" onClick={() => setShowImportar(true)}
                  className="px-4 py-2.5 rounded-md border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10 font-medium flex items-center gap-2"
                  title="Importar itens de planilha CSV ou Excel">
            <UploadSimple size={14} weight="duotone" /> Importar Planilha
          </button>
          <button data-testid="pac-gerar-previo" onClick={gerarPrevio}
                  className="px-4 py-2.5 rounded-md border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 font-medium flex items-center gap-2"
                  title="Selecionar contratos ativos e gerar PAC prévio">
            <MagicWand size={14} weight="duotone" /> Gerar PAC Prévio
          </button>
          <button data-testid="pac-textos-ia" onClick={() => setShowTextosIA(true)}
                  className="px-4 py-2.5 rounded-md border border-[#FFDF00]/40 text-[#FFDF00] hover:bg-[#FFDF00]/10 font-medium flex items-center gap-2"
                  title="Editar ou gerar com IA os textos institucionais do PCA (Introdução, Análise Executiva, Considerações Finais)">
            <Robot size={14} weight="duotone" /> Textos IA
          </button>
          <button data-testid="pac-dashboard" onClick={() => setShowDashboard(true)}
                  className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#8B5CF6] text-[#E9EEF5] font-medium flex items-center gap-2">
            <ChartBar size={14} weight="duotone" /> Consolidado {ano}
          </button>
          <button data-testid="new-pac" onClick={() => { setEditing(null); setShowForm(true); }}
                  className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium flex items-center gap-2">
            <Plus size={16} weight="bold" /> Novo item
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { l: "Ano do plano", v: ano, i: Calendar, c: "#F59E0B" },
          { l: "Itens totais", v: total, i: FileText, c: "#3B82F6" },
          { l: "Publicados / Rascunhos", v: `${totais.publicados} / ${totais.rascunhos}`, i: CheckCircle, c: "#10B981" },
          { l: "Valor previsto total", v: fmtBRL(totais.total_previsto), i: CurrencyDollar, c: "#8B5CF6" },
        ].map((s, i) => (
          <div key={i} className="tce-card p-5">
            <div className="uppercase-label flex items-center gap-1.5" style={{ color: s.c }}>
              <s.i size={12} weight="duotone" /> {s.l}
            </div>
            <div className="font-heading text-2xl mt-2 text-[#E9EEF5]">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="tce-card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="uppercase-label block mb-2">Ano</label>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
                    className="tce-input px-3 py-2.5 rounded-md text-sm" data-testid="pac-ano">
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Categoria</label>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                    className="tce-input px-3 py-2.5 rounded-md text-sm" data-testid="pac-categoria-filter">
              <option value="">Todas</option>
              {CATEGORIAS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="tce-input px-3 py-2.5 rounded-md text-sm" data-testid="pac-status-filter">
              <option value="">Todos</option>
              <option value="rascunho">Rascunho</option>
              <option value="publicado">Publicado</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="uppercase-label block mb-2">Buscar</label>
            <div className="relative">
              <MagnifyingGlass size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pac-search"
                     placeholder="Objeto, área, justificativa…"
                     className="tce-input w-full pl-9 pr-3 py-2.5 rounded-md text-sm" />
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => exportar("csv")} title="Exportar CSV" data-testid="pac-export-csv"
                    className="px-3 py-2.5 rounded-md border border-[#334155] hover:border-[#3B82F6] text-[#3B82F6] text-xs font-medium flex items-center gap-1">
              <FileText size={12} /> CSV
            </button>
            <button onClick={() => exportar("excel")} title="Exportar Excel" data-testid="pac-export-excel"
                    className="px-3 py-2.5 rounded-md border border-[#334155] hover:border-[#10B981] text-[#10B981] text-xs font-medium flex items-center gap-1">
              <FileXls size={12} /> Excel
            </button>
            <button onClick={() => exportar("pdf")} title="Exportar PDF simples" data-testid="pac-export-pdf"
                    className="px-3 py-2.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-[#F59E0B] text-xs font-medium flex items-center gap-1">
              <FilePdf size={12} /> PDF
            </button>
            <button onClick={() => exportar("apresentacao")} title="Baixar apresentação institucional em PDF (capa, gráficos, indicadores, cronograma, assinaturas)" data-testid="pac-export-apresentacao"
                    className="px-3 py-2.5 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-xs font-semibold flex items-center gap-1 shadow-sm hover:shadow-md transition-shadow">
              <FilePdf size={12} weight="fill" /> Apresentação PCA
            </button>
          </div>
        </div>
      </div>

      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-4 py-3 uppercase-label">Nº</th>
                <th className="px-4 py-3 uppercase-label">Categoria</th>
                <th className="px-4 py-3 uppercase-label">Objeto</th>
                <th className="px-4 py-3 uppercase-label">Qtd/Un.</th>
                <th className="px-4 py-3 uppercase-label">V. Total</th>
                <th className="px-4 py-3 uppercase-label">Mês</th>
                <th className="px-4 py-3 uppercase-label">Área</th>
                <th className="px-4 py-3 uppercase-label">Status</th>
                <th className="px-4 py-3 uppercase-label text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-[#94A3B8]">
                  Nenhum item cadastrado no PAC {ano}. Clique em "Novo item" para começar.
                </td></tr>
              ) : items.map((it, i) => (
                <tr key={it.id} data-testid={`pac-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                  <td className="px-4 py-3 font-mono text-[#F59E0B]">{String(it.numero_seq).padStart(3, "0")}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                          style={{ background: `${catColor[it.categoria]}15`, color: catColor[it.categoria] }}>
                      {CATEGORIAS.find((c) => c.v === it.categoria)?.l || it.categoria}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#E9EEF5] max-w-[300px] truncate" title={it.descricao_objeto}>
                    {it.descricao_objeto}
                    {it.origem === "pac_previo_auto" && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]"
                            title="Item gerado automaticamente a partir de contrato ativo">
                        <MagicWand size={9} weight="fill" /> AUTO
                      </span>
                    )}
                    {it.contract_ids?.length > 0 && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-[#10B981]">
                        <LinkSimple size={10} /> {it.contract_ids.length} contrato(s)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8] text-xs">{it.quantidade} {it.unidade_medida}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#FCD34D]">{fmtBRL(it.valor_total_estimado)}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">{MESES[it.mes_previsto - 1]}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]">{it.area_requisitante || "—"}</td>
                  <td className="px-4 py-3">
                    {it.status === "publicado" ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#10B981]/15 text-[#10B981]">
                        <CheckCircle size={11} weight="fill" /> Publicado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#94A3B8]/15 text-[#94A3B8]">
                        Rascunho
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => navigate(`/editor/pac/${it.id}`)} data-testid={`pac-editor-${i}`}
                              title="Editor Inteligente da justificativa (com IA)"
                              className="p-1.5 rounded text-[#94A3B8] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10">
                        <Robot size={14} weight="duotone" />
                      </button>
                      <button onClick={() => { setEditing(it); setShowForm(true); }} data-testid={`pac-edit-${i}`}
                              title="Editar item" disabled={it.status === "publicado" && !isAdmin}
                              className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 disabled:opacity-30">
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      {it.status === "rascunho" && (
                        <button onClick={() => publicar(it)} data-testid={`pac-publish-${i}`}
                                title="Publicar item"
                                className="p-1.5 rounded text-[#94A3B8] hover:text-[#10B981] hover:bg-[#10B981]/10">
                          <CheckCircle size={14} weight="duotone" />
                        </button>
                      )}
                      <button onClick={() => excluir(it)} data-testid={`pac-delete-${i}`}
                              title="Excluir" disabled={it.status === "publicado" && !isAdmin}
                              className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-30">
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <PACForm initial={editing} ano={ano} isAdmin={isAdmin} userMunicipioId={user?.municipio_id}
                             onClose={() => { setShowForm(false); setEditing(null); }}
                             onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      {showDashboard && <PACDashboard ano={ano} onClose={() => setShowDashboard(false)} />}
      {showImportar && <ImportarModal ano={ano} isAdmin={isAdmin} userMunicipioId={user?.municipio_id}
                                       onClose={() => setShowImportar(false)}
                                       onDone={() => { setShowImportar(false); load(); }} />}
      {showSelector && <SelecionarContratosModal ano={ano} isAdmin={isAdmin} userMunicipioId={user?.municipio_id}
                                                    onClose={() => setShowSelector(false)}
                                                    onDone={() => { setShowSelector(false); load(); }} />}
      {showTextosIA && <TextosPACModal ano={ano} isAdmin={isAdmin} userMunicipioId={user?.municipio_id}
                                        onClose={() => setShowTextosIA(false)} />}
    </div>
  );
}

function PACForm({ initial, ano, isAdmin, userMunicipioId, onClose, onSaved }) {
  const [municipios, setMunicipios] = useState([]);
  const [form, setForm] = useState({
    municipio_id: initial?.municipio_id || userMunicipioId || "",
    ano: initial?.ano || ano,
    categoria: initial?.categoria || "servico",
    descricao_objeto: initial?.descricao_objeto || "",
    quantidade: initial?.quantidade || 1,
    unidade_medida: initial?.unidade_medida || "UN",
    valor_unitario_estimado: initial?.valor_unitario_estimado || 0,
    mes_previsto: initial?.mes_previsto || 1,
    justificativa: initial?.justificativa || "",
    catalogo_pncp_codigo: initial?.catalogo_pncp_codigo || "",
    catalogo_pncp_descricao: initial?.catalogo_pncp_descricao || "",
    area_requisitante: initial?.area_requisitante || "",
  });
  const [saving, setSaving] = useState(false);
  const [pncpSearch, setPncpSearch] = useState("");
  const [pncpResults, setPncpResults] = useState([]);
  const [searchingPncp, setSearchingPncp] = useState(false);

  useEffect(() => { if (isAdmin) api.get("/municipalities?limit=500").then((r) => setMunicipios(r.data || [])); }, [isAdmin]);

  const buscarPncp = async () => {
    if (!pncpSearch || pncpSearch.length < 3) { toast.error("Digite ao menos 3 caracteres"); return; }
    setSearchingPncp(true);
    try {
      const { data } = await api.get(`/pac/pncp-busca?q=${encodeURIComponent(pncpSearch)}&limit=15`);
      setPncpResults(data.items || []);
      if ((data.items || []).length === 0) toast.info("Nenhum item encontrado no catálogo PNCP local");
    } catch { toast.error("Erro na busca PNCP"); } finally { setSearchingPncp(false); }
  };

  const aplicarPncp = (r) => {
    setForm((f) => ({
      ...f,
      descricao_objeto: r.descricao_item || f.descricao_objeto,
      unidade_medida: r.unidade_medida || f.unidade_medida,
      valor_unitario_estimado: r.valor_unitario || f.valor_unitario_estimado,
      catalogo_pncp_codigo: r.codigo_item_catalogo || "",
      catalogo_pncp_descricao: r.descricao_item || "",
    }));
    setPncpResults([]);
    toast.success("Item aplicado do catálogo PNCP");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.municipio_id || !form.descricao_objeto || !form.justificativa) { toast.error("Preencha os campos obrigatórios"); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.patch(`/pac/${initial.id}`, form);
        toast.success("Item atualizado");
      } else {
        await api.post("/pac", form);
        toast.success("Item cadastrado");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">{initial ? "Editar item" : "Novo item do PAC"}</div>
            <div className="font-heading text-xl mt-1">Plano Anual de Contratações {form.ano}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]" data-testid="pac-form-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {isAdmin && (
            <div>
              <label className="uppercase-label block mb-2">Município *</label>
              <select required value={form.municipio_id} onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                      data-testid="pac-municipio" className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="">Selecione…</option>
                {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
              </select>
            </div>
          )}

          <div className="rounded-md bg-[#3B82F6]/5 border border-[#3B82F6]/30 p-4 space-y-3">
            <div className="uppercase-label flex items-center gap-1.5 text-[#3B82F6]">
              <MagnifyingGlass size={12} weight="duotone" /> Busca no catálogo PNCP (opcional)
            </div>
            <div className="flex gap-2">
              <input value={pncpSearch} onChange={(e) => setPncpSearch(e.target.value)}
                     placeholder="Ex.: papel A4, cartucho toner, serviço de limpeza…"
                     className="tce-input flex-1 px-3 py-2 rounded-md text-sm" data-testid="pac-pncp-search" />
              <button type="button" onClick={buscarPncp} disabled={searchingPncp}
                      className="px-3 py-2 rounded-md bg-[#3B82F6] text-white text-sm disabled:opacity-40" data-testid="pac-pncp-btn">
                {searchingPncp ? "Buscando…" : "Buscar"}
              </button>
            </div>
            {pncpResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1 border-t border-[#334155] pt-2">
                {pncpResults.map((r, i) => (
                  <button type="button" key={i} onClick={() => aplicarPncp(r)}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-[#3B82F6]/10 text-xs">
                    <div className="text-[#E9EEF5] truncate">{r.descricao_item}</div>
                    <div className="text-[10px] text-[#94A3B8]">{r.unidade_medida} · {fmtBRL(r.valor_unitario)} · {r.fonte}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Categoria *</label>
              <select required value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      data-testid="pac-categoria" className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                {CATEGORIAS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div>
              <label className="uppercase-label block mb-2">Mês previsto *</label>
              <select required value={form.mes_previsto} onChange={(e) => setForm({ ...form, mes_previsto: parseInt(e.target.value, 10) })}
                      data-testid="pac-mes" className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                {MESES.map((m, i) => <option key={i} value={i + 1}>{m} ({String(i + 1).padStart(2, "0")})</option>)}
              </select>
            </div>
            <div>
              <label className="uppercase-label block mb-2">Área requisitante</label>
              <input value={form.area_requisitante} onChange={(e) => setForm({ ...form, area_requisitante: e.target.value })}
                     placeholder="Ex.: SEMSUR" className="tce-input w-full px-3 py-2.5 rounded-md text-sm" data-testid="pac-area" />
            </div>
          </div>

          <div>
            <label className="uppercase-label block mb-2">Descrição do objeto *</label>
            <textarea required rows={2} value={form.descricao_objeto} onChange={(e) => setForm({ ...form, descricao_objeto: e.target.value })}
                      data-testid="pac-descricao" className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
                      placeholder="Ex.: Aquisição de material de escritório para 2026…" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Quantidade *</label>
              <input type="number" step="0.01" min="0.01" required value={form.quantidade}
                     onChange={(e) => setForm({ ...form, quantidade: parseFloat(e.target.value) || 0 })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" data-testid="pac-quantidade" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Unidade *</label>
              <input required value={form.unidade_medida} onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}
                     placeholder="UN / KG / MES / M2…" className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono uppercase" data-testid="pac-unidade" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Valor unitário R$ *</label>
              <input type="number" step="0.01" min="0" required value={form.valor_unitario_estimado}
                     onChange={(e) => setForm({ ...form, valor_unitario_estimado: parseFloat(e.target.value) || 0 })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" data-testid="pac-valor" />
            </div>
          </div>

          <div className="rounded-md bg-[#F59E0B]/5 border border-[#F59E0B]/30 p-3 text-sm">
            <span className="text-[#94A3B8]">Valor total estimado: </span>
            <span className="text-[#FCD34D] font-mono font-bold">{fmtBRL(form.quantidade * form.valor_unitario_estimado)}</span>
          </div>

          <div>
            <label className="uppercase-label block mb-2">Justificativa da necessidade *</label>
            <textarea required rows={3} value={form.justificativa} onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                      data-testid="pac-justificativa" className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
                      placeholder="Explique a necessidade da contratação…" />
            <div className="text-[10px] text-[#94A3B8] mt-1">
              Você pode revisar/expandir esta justificativa com IA depois via botão do robô na lista.
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">Cancelar</button>
            <button data-testid="pac-form-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Salvando…" : (initial ? "Atualizar" : "Cadastrar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PACDashboard({ ano, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/pac/consolidado?ano=${ano}`).then((r) => setData(r.data)); }, [ano]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label flex items-center gap-1.5"><ChartBar size={12} weight="duotone" className="text-[#8B5CF6]" /> Consolidado Anual</div>
            <div className="font-heading text-2xl mt-1">PAC {ano} · Previsto vs Executado</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {!data ? <div className="text-center py-8 text-[#94A3B8]">Carregando…</div> : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {[
                  { l: "Total de itens", v: data.totais.itens, c: "#3B82F6" },
                  { l: "Publicados", v: data.totais.publicados, c: "#10B981" },
                  { l: "Já contratados", v: data.totais.contratados, c: "#F59E0B" },
                  { l: "% Executado", v: `${data.totais.percentual_executado.toFixed(1)}%`, c: "#8B5CF6" },
                ].map((s, i) => (
                  <div key={i} className="rounded-md bg-[#0F172A] border border-[#334155] p-4">
                    <div className="uppercase-label" style={{ color: s.c }}>{s.l}</div>
                    <div className="font-heading text-2xl mt-2 text-[#E9EEF5]">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-md bg-[#F59E0B]/5 border border-[#F59E0B]/30 p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#94A3B8]">Total previsto</span>
                  <span className="text-[#FCD34D] font-mono font-bold">{fmtBRL(data.totais.valor_previsto)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#94A3B8]">Total executado</span>
                  <span className="text-[#10B981] font-mono font-bold">{fmtBRL(data.totais.valor_executado)}</span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-[#334155] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#10B981] to-[#F59E0B]"
                       style={{ width: `${Math.min(data.totais.percentual_executado, 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="uppercase-label mb-3">Por categoria</div>
                <div className="space-y-2">
                  {data.por_categoria.length === 0 ? (
                    <div className="text-center py-6 text-[#94A3B8]">Nenhum item no PAC {ano}.</div>
                  ) : data.por_categoria.map((cat) => (
                    <div key={cat.categoria} className="rounded-md bg-[#0F172A] border border-[#334155] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ background: catColor[cat.categoria] }} />
                          <span className="font-medium text-[#E9EEF5]">{cat.categoria_label}</span>
                          <span className="text-xs text-[#94A3B8]">· {cat.total_itens} item(s)</span>
                        </div>
                        <span className="text-sm font-mono text-[#94A3B8]">{cat.percentual_executado.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[#94A3B8]">Previsto: <span className="text-[#FCD34D] font-mono">{fmtBRL(cat.valor_previsto)}</span></span>
                        <span className="text-[#94A3B8]">Executado: <span className="text-[#10B981] font-mono">{fmtBRL(cat.valor_executado)}</span></span>
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-[#334155] overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.min(cat.percentual_executado, 100)}%`, background: catColor[cat.categoria] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportarModal({ ano, isAdmin, userMunicipioId, onClose, onDone }) {
  const [municipios, setMunicipios] = useState([]);
  const [municipioId, setMunicipioId] = useState(userMunicipioId || "");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (isAdmin) api.get("/municipalities?limit=500").then((r) => setMunicipios(r.data || [])); }, [isAdmin]);

  const baixarTemplate = async () => {
    const res = await fetch(`${API}/pac-template`, { credentials: "include" });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "modelo_pac.xlsx"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const importar = async () => {
    if (!file || !municipioId) { toast.error("Selecione município e arquivo"); return; }
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("municipio_id", municipioId); fd.append("ano", ano);
      const { data } = await api.post("/pac/importar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(data.mensagem);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro na importação"); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label flex items-center gap-1.5"><UploadSimple size={12} weight="duotone" className="text-[#3B82F6]" /> Importar Planilha</div>
            <div className="font-heading text-xl mt-1">Itens do PAC {ano}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-md bg-[#3B82F6]/5 border border-[#3B82F6]/30 p-4">
            <div className="text-sm text-[#E9EEF5] mb-2">Formato aceito: <strong>.xlsx</strong> ou <strong>.csv</strong> (separador ; ou ,)</div>
            <div className="text-xs text-[#94A3B8] mb-3">
              Colunas obrigatórias: <code>categoria, descricao_objeto, quantidade, unidade_medida, valor_unitario_estimado, mes_previsto, justificativa</code>. Opcionais: <code>area_requisitante, catalogo_pncp_codigo</code>.
            </div>
            <button type="button" onClick={baixarTemplate} data-testid="pac-baixar-template"
                    className="px-3 py-1.5 rounded-md border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10 text-xs font-medium flex items-center gap-1.5">
              <DownloadSimple size={12} weight="duotone" /> Baixar modelo XLSX
            </button>
          </div>
          {isAdmin && (
            <div>
              <label className="uppercase-label block mb-2">Município *</label>
              <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)} data-testid="importar-municipio"
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                <option value="">Selecione…</option>
                {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="uppercase-label block mb-2">Arquivo (XLSX / CSV) *</label>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="importar-file"
                   className="tce-input w-full px-3 py-2 rounded-md text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-[#F59E0B] file:text-[#0F172A] file:cursor-pointer" />
          </div>
          {result && (
            <div className="rounded-md bg-[#10B981]/5 border border-[#10B981]/30 p-3 text-sm">
              <div className="text-[#10B981] font-medium">{result.mensagem}</div>
              {result.erros?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-[#EF4444]">Ver {result.erros.length} erro(s)</summary>
                  <ul className="mt-1 text-xs text-[#94A3B8]">
                    {result.erros.slice(0, 10).map((e, i) => <li key={i}>Linha {e.linha}: {e.erro}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={result ? onDone : onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              {result ? "Concluir" : "Cancelar"}
            </button>
            {!result && (
              <button onClick={importar} disabled={importing || !file || !municipioId} data-testid="pac-importar-submit"
                      className="px-5 py-2 rounded-md bg-[#3B82F6] text-white font-medium text-sm disabled:opacity-50">
                {importing ? "Importando…" : "Importar itens"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SelecionarContratosModal({ ano, isAdmin, userMunicipioId, onClose, onDone }) {
  const [municipios, setMunicipios] = useState([]);
  const [municipioId, setMunicipioId] = useState(userMunicipioId || "");
  const [contratos, setContratos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [busca, setBusca] = useState("");
  useEffect(() => { if (isAdmin) api.get("/municipalities?limit=500").then((r) => setMunicipios(r.data || [])); }, [isAdmin]);

  const carregar = async () => {
    if (!municipioId) { toast.error("Selecione o município"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/pac-contratos-elegiveis?municipio_id=${municipioId}&ano=${ano}`);
      setContratos(data.items || []);
      // pré-selecionar apenas contratos NÃO vinculados
      setSelected(new Set((data.items || []).filter((c) => !c.ja_no_pac).map((c) => c.id)));
    } catch { toast.error("Erro ao carregar contratos"); }
    finally { setLoading(false); }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === contratos.filter((c) => !c.ja_no_pac).length) setSelected(new Set());
    else setSelected(new Set(contratos.filter((c) => !c.ja_no_pac).map((c) => c.id)));
  };

  const gerar = async () => {
    if (selected.size === 0) { toast.error("Selecione ao menos um contrato"); return; }
    setProcessing(true);
    try {
      const { data } = await api.post("/pac/gerar-previo", { municipio_id: municipioId, ano, contract_ids: Array.from(selected) });
      toast.success(`${data.criados} item(ns) criado(s) · ${data.ignorados} ignorado(s)`, { duration: 6000 });
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao gerar"); }
    finally { setProcessing(false); }
  };

  const filtrados = contratos.filter((c) =>
    !busca || (c.objeto || "").toLowerCase().includes(busca.toLowerCase()) || (c.numero || "").includes(busca)
  );
  const totalSelecionado = filtrados.filter((c) => selected.has(c.id)).reduce((s, c) => s + (c.valor || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155] flex-shrink-0">
          <div>
            <div className="uppercase-label flex items-center gap-1.5"><MagicWand size={12} weight="duotone" className="text-[#10B981]" /> Gerar PAC Prévio</div>
            <div className="font-heading text-xl mt-1">Selecionar contratos para PAC {ano}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3 flex-shrink-0 border-b border-[#334155]">
          {isAdmin && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="uppercase-label block mb-2">Município *</label>
                <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)} data-testid="selector-muni"
                        className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                  <option value="">Selecione…</option>
                  {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
                </select>
              </div>
              <button onClick={carregar} disabled={!municipioId || loading} data-testid="selector-load"
                      className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium text-sm disabled:opacity-50">
                {loading ? "Buscando…" : "Buscar contratos"}
              </button>
            </div>
          )}
          {!isAdmin && contratos.length === 0 && (
            <button onClick={carregar} disabled={loading} data-testid="selector-load"
                    className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium text-sm disabled:opacity-50">
              {loading ? "Buscando…" : "Buscar contratos ativos"}
            </button>
          )}
          {contratos.length > 0 && (
            <>
              <div className="flex gap-2 items-center">
                <input value={busca} onChange={(e) => setBusca(e.target.value)}
                       placeholder="Filtrar por objeto ou nº do contrato…"
                       className="tce-input flex-1 px-3 py-2 rounded-md text-sm" />
                <button onClick={toggleAll} className="px-3 py-2 rounded-md border border-[#334155] text-xs">
                  {selected.size === contratos.filter((c) => !c.ja_no_pac).length ? "Desmarcar" : "Marcar"} todos
                </button>
              </div>
              <div className="text-xs text-[#94A3B8]">
                {contratos.length} contrato(s) ativo(s) · <span className="text-[#F59E0B]">{selected.size} selecionado(s)</span> · Valor total: <span className="text-[#FCD34D] font-mono">{fmtBRL(totalSelecionado)}</span>
              </div>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {contratos.length === 0 && !loading && (
            <div className="text-center py-8 text-[#94A3B8]">Selecione um município e clique em "Buscar contratos".</div>
          )}
          {filtrados.map((c) => (
            <div key={c.id} onClick={() => !c.ja_no_pac && toggle(c.id)}
                 className={`px-3 py-2 rounded-md mb-1 flex items-center gap-3 border transition-colors cursor-pointer ${
                   c.ja_no_pac ? "opacity-40 border-[#334155] cursor-not-allowed" :
                   selected.has(c.id) ? "bg-[#10B981]/10 border-[#10B981]/40" : "border-[#334155] hover:border-[#94A3B8]"}`}>
              {c.ja_no_pac ? (
                <CheckSquare size={16} weight="fill" className="text-[#94A3B8] flex-shrink-0" />
              ) : selected.has(c.id) ? (
                <CheckSquare size={16} weight="fill" className="text-[#10B981] flex-shrink-0" />
              ) : (
                <Square size={16} className="text-[#94A3B8] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#E9EEF5] truncate">
                  <span className="font-mono text-[#F59E0B]">{c.numero}</span> · {(c.objeto || "").slice(0, 100)}
                  {c.ja_no_pac && <span className="ml-2 text-[10px] text-[#94A3B8]">(já no PAC)</span>}
                </div>
                <div className="text-[10px] text-[#94A3B8]">
                  {c.fornecedor} · Vigência até {(c.data_fim || "—").slice(0, 10)} · Categoria sugerida: <span className="text-[#3B82F6]">{c.categoria_sugerida}</span>
                </div>
              </div>
              <div className="text-sm font-mono text-[#FCD34D]">{fmtBRL(c.valor)}</div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-[#334155] flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm">Cancelar</button>
          <button onClick={gerar} disabled={selected.size === 0 || processing} data-testid="selector-gerar"
                  className="px-5 py-2 rounded-md bg-[#10B981] text-white font-medium text-sm disabled:opacity-50 flex items-center gap-2">
            <MagicWand size={14} weight="bold" /> {processing ? "Gerando…" : `Gerar ${selected.size} item(ns) do PAC`}
          </button>
        </div>
      </div>
    </div>
  );
}



/* ============================================================
 * TextosPACModal — Editor institucional dos textos do PCA (IA + edição manual)
 * ============================================================ */
function TextosPACModal({ ano, isAdmin, userMunicipioId, onClose }) {
  const [municipios, setMunicipios] = useState([]);
  const [municipioId, setMunicipioId] = useState(userMunicipioId || "");
  const [textos, setTextos] = useState({ introducao_html: "", analise_html: "", consideracoes_html: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [tab, setTab] = useState("introducao");
  const [instrucoes, setInstrucoes] = useState("");
  const [meta, setMeta] = useState({ updated_at: null, ai_generated_at: null });

  useEffect(() => {
    if (isAdmin) {
      api.get("/municipalities").then((r) => setMunicipios(r.data || []));
    }
  }, [isAdmin]);

  const carregar = () => {
    if (!municipioId) return;
    setLoading(true);
    api.get(`/pac-textos?ano=${ano}&municipio_id=${municipioId}`)
      .then((r) => {
        setTextos({
          introducao_html: r.data.introducao_html || "",
          analise_html: r.data.analise_html || "",
          consideracoes_html: r.data.consideracoes_html || "",
        });
        setMeta({ updated_at: r.data.updated_at, ai_generated_at: r.data.ai_generated_at });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Erro ao carregar textos"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [municipioId, ano]);

  const salvar = async () => {
    if (!municipioId) { toast.error("Selecione um município"); return; }
    setSaving(true);
    try {
      const r = await api.put("/pac-textos", {
        municipio_id: municipioId, ano,
        introducao_html: textos.introducao_html,
        analise_html: textos.analise_html,
        consideracoes_html: textos.consideracoes_html,
      });
      setMeta({ updated_at: r.data.updated_at, ai_generated_at: r.data.ai_generated_at });
      toast.success("Textos salvos com sucesso");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const gerarIA = async (secoes) => {
    if (!municipioId) { toast.error("Selecione um município"); return; }
    setGenerating(secoes[0] === "todas" ? "todas" : secoes[0]);
    try {
      const secoesReq = secoes[0] === "todas" ? ["introducao", "analise", "consideracoes"] : secoes;
      const r = await api.post("/pac-textos/gerar-ia", {
        municipio_id: municipioId, ano,
        secoes: secoesReq,
        instrucoes_adicionais: instrucoes || null,
      });
      setTextos({
        introducao_html: r.data.introducao_html || "",
        analise_html: r.data.analise_html || "",
        consideracoes_html: r.data.consideracoes_html || "",
      });
      setMeta({ updated_at: r.data.updated_at, ai_generated_at: r.data.ai_generated_at });
      toast.success("Texto gerado pela IA — revise e salve");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao gerar via IA";
      toast.error(msg.length > 180 ? msg.slice(0, 180) + "…" : msg);
    } finally { setGenerating(null); }
  };

  const TABS = [
    { k: "introducao", l: "Introdução", field: "introducao_html", aiKey: "introducao" },
    { k: "analise", l: "Análise Executiva", field: "analise_html", aiKey: "analise" },
    { k: "consideracoes", l: "Considerações Finais", field: "consideracoes_html", aiKey: "consideracoes" },
  ];
  const currentTab = TABS.find((t) => t.k === tab);
  const currentHtml = textos[currentTab.field];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl h-[92vh] bg-[#1E293B] border border-[#334155] rounded-lg flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between gap-4 flex-shrink-0">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <Robot size={12} weight="duotone" className="text-[#FFDF00]" /> Textos institucionais · PCA {ano}
            </div>
            <h2 className="font-heading text-2xl mt-1">Editor + IA dos Textos do PCA</h2>
            <p className="text-xs text-[#94A3B8] mt-1">
              Introdução, Análise Executiva e Considerações Finais que aparecem no <b>PDF Apresentação PCA</b>.
              Personalize manualmente ou gere automaticamente via IA (Claude Sonnet 4.5) usando o perfil real das contratações do município.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded" data-testid="close-textos">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-[#334155] flex flex-wrap gap-3 items-end flex-shrink-0">
          {isAdmin && (
            <div>
              <label className="uppercase-label block mb-1">Município *</label>
              <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)}
                      className="tce-input px-3 py-2 rounded-md text-sm" data-testid="textos-muni">
                <option value="">Selecione…</option>
                {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex-1 min-w-[280px]">
            <label className="uppercase-label block mb-1">Instruções adicionais para a IA (opcional)</label>
            <input value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)}
                   placeholder='Ex.: "Enfatize investimentos em saúde e educação"'
                   className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="textos-instrucoes" />
          </div>
          <button onClick={() => gerarIA(["todas"])} disabled={!municipioId || generating}
                  className="px-4 py-2 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                  data-testid="textos-gerar-todas" title="Gerar as 3 seções via IA">
            <MagicWand size={14} weight="bold" /> {generating === "todas" ? "Gerando…" : "Gerar Todas com IA"}
          </button>
        </div>

        <div className="border-b border-[#334155] px-4 flex gap-1 flex-shrink-0">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      tab === t.k ? "border-[#FFDF00] text-[#FFDF00]" : "border-transparent text-[#94A3B8] hover:text-[#E9EEF5]"
                    }`}
                    data-testid={`textos-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-[#94A3B8] text-sm">Carregando textos…</div>
          ) : !municipioId ? (
            <div className="flex-1 flex items-center justify-center text-[#94A3B8] text-sm">
              Selecione um município para começar a editar
            </div>
          ) : (
            <>
              <div className="p-4 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="text-xs text-[#94A3B8]">
                  {meta.ai_generated_at && (
                    <>
                      <Robot size={11} weight="duotone" className="inline text-[#FFDF00] mr-1" />
                      IA em {new Date(meta.ai_generated_at).toLocaleString("pt-BR")}
                      {meta.updated_at && meta.updated_at !== meta.ai_generated_at && " · editado após a geração"}
                    </>
                  )}
                  {!meta.ai_generated_at && meta.updated_at && (
                    <>
                      <PencilSimple size={11} weight="duotone" className="inline text-[#3B82F6] mr-1" />
                      Editado em {new Date(meta.updated_at).toLocaleString("pt-BR")}
                    </>
                  )}
                  {!meta.updated_at && "Nenhum texto salvo ainda — usaremos o texto padrão no PDF."}
                </div>
                <button onClick={() => gerarIA([currentTab.aiKey])} disabled={generating}
                        className="px-3 py-1.5 rounded-md border border-[#FFDF00]/50 text-[#FFDF00] hover:bg-[#FFDF00]/10 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`textos-gerar-${currentTab.k}`}
                        title={`Gerar apenas "${currentTab.l}" via IA`}>
                  <MagicWand size={12} weight="bold" />
                  {generating === currentTab.aiKey ? "Gerando…" : `Gerar ${currentTab.l} com IA`}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <textarea
                  value={currentHtml}
                  onChange={(e) => setTextos({ ...textos, [currentTab.field]: e.target.value })}
                  placeholder={'Escreva a ' + currentTab.l.toLowerCase() + ' em HTML simples (<p>, <strong>, <em>, <ul>, <li>). Ou clique em "Gerar com IA".'}
                  className="w-full h-[300px] bg-[#0F172A] border border-[#334155] rounded-md p-4 text-sm text-[#E9EEF5] font-mono resize-none focus:border-[#FFDF00] focus:outline-none"
                  data-testid={`textos-editor-${currentTab.k}`}
                />
                {currentHtml && (
                  <div className="mt-4">
                    <div className="uppercase-label mb-2">Prévia (como aparecerá no PDF)</div>
                    <div className="bg-[#0F172A] border border-[#334155] rounded-md p-5 text-sm text-[#E9EEF5] leading-relaxed ai-response"
                         dangerouslySetInnerHTML={{ __html: currentHtml }} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-[#334155] flex items-center justify-between gap-2 flex-shrink-0">
          <div className="text-xs text-[#94A3B8]">
            Os textos salvos são aplicados automaticamente no PDF <b>Apresentação PCA</b> do município selecionado.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm" data-testid="textos-cancelar">
              Cancelar
            </button>
            <button onClick={salvar} disabled={!municipioId || saving}
                    className="px-5 py-2 rounded-md bg-[#009C3B] text-white font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                    data-testid="textos-salvar">
              <CheckCircle size={14} weight="bold" /> {saving ? "Salvando…" : "Salvar textos"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
